import React, { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { LocalSignInContext } from "../../../app/contexts/LocalSignInContext";
import { getUserInfo } from "../../../app/webUXApi/userInfoApi";
import { Measures, addMarkerToPageTimings } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { ContentReadyContext } from "../../contexts/ContentReadyContext";
import { CtaSettingContext } from "../../contexts/CtaSettingContext";
import { addIdentityStatusChangedEventListener, addReauthRequiredStatusChangedEventListener, getIsAccountAvailable, getSignInStatus, removeIdentityStatusChangedEventListener, removeReauthRequiredStatusChangedEventListener } from "../../externalApi/AuthenticatorApi";
import { notifyContentDisplayReady, requestRefresh } from "../../externalApi/DashboardHostApi";
import { addNetworkStatusChangedEventListener, isInCTARegion, isInternetConnectionAvailable, isPrivacyConsentAccepted, networkCostConstraint, removeNetworkStatusChangedEventListener } from "../../externalApi/DashboardHostApiInternal";
import { tryGetHostApi, } from "../../externalApi/externalApi";
import { getWidgetOrchestrator } from "../../widgetRuntime/orchestrator/orchestrator";
import { EmptyPage } from "../Pages/EmptyPage/EmptyPage";
import { isFeatureEnabled } from "../../externalApi/VelocityApi";
import { setIsMeteredConnectionUsageAllowedForMigration } from "../../externalApi/DashboardHostApi";
const GlobalOfflinePage = React.lazy(() => import(
/* webpackChunkName: "GlobalOfflinePage-Chunk" */
"../Pages/GlobalOfflinePage/GlobalOfflinePage"));
const CTAOfflinePage = React.lazy(() => import(
/* webpackChunkName: "CTAOfflinePage-Chunk" */
"../Pages/CTAOfflinePage/CTAOfflinePage"));
const LocationNetworkCTAPage = React.lazy(() => import(
/* webpackChunkName: "LocationNetworkCTAPage-Chunk" */
"../Pages/LocationNetworkCTAPage/LocationNetworkCTAPage"));
const TempDisablementPage = React.lazy(() => import(
/* webpackChunkName: "TempDisablementPage-Chunk" */
"../Pages/TempDisablementPage/TempDisablementPage"));
const MainPage = React.lazy(() => import(
/* webpackChunkName: "MainPage-Chunk" */
"../Pages/MainPage/MainPage"));
let prevPageManagerUnmountedResolver = null;
let hasPrevPageManagerUnmountedPromise = null;
let currentPageManagerMountedResolver = null;
let hasCurrentPageManagerMountedPromise = null;
function generatePromise(isMountLifeCycle = false) {
    return new Promise(resolve => {
        if (isMountLifeCycle) {
            prevPageManagerUnmountedResolver = resolve;
        }
        else {
            currentPageManagerMountedResolver = resolve;
        }
    });
}
// The PageManager component owns the state and logic used to determine what page of the WebUX to render based on various signals.
export const PageManager = ({ isAugmentedEntryPointEnabled, isAvoidRenderWhileHiddenEnabled, onClick }) => {
    var _a;
    addMarkerToPageTimings("MEE.PageManagerStart");
    const page = useRef("empty-page");
    const [isPrivacyConsent, setIsPrivacyConsent] = useState(null);
    const [isInCtaRegion, setIsInCtaRegion] = useState(null);
    const persistedIsMeteredConnectionUsageAllowed = window.localStorage.getItem("isMeteredConnectionUsageAllowed");
    const [isMeteredConnectionUsageAllowed, setIsMeteredConnectionUsageAllowed] = useState(persistedIsMeteredConnectionUsageAllowed === "true");
    const [isOnlineAccount, setIsOnlineAccount] = useState(null);
    const [isUserCancelMeteredConnection, setIsUserCancelMeteredConnection] = useState(false);
    const [isInternetConnection, setIsInternetConnection] = useState(null);
    const [networkCostConstraintState, setNetworkCostConstraintState] = useState(null);
    const [userInfo, setUserInfo] = useImmer(undefined);
    const resetMainPage = useRef();
    const [isReauthRequired, setIsReauthRequired] = useState(false);
    const authenticator = (_a = window.__com_microsoft_dsh_widgetModelApiFactory) === null || _a === void 0 ? void 0 : _a.tryGetApi("authenticator", 1, 1);
    // We'll update to the correct state during useEffect.
    const [isSignedIn, setIsSignedIn] = useState(true);
    let signInState = 0 /* Unknown */;
    function updateIsPrivacyConsented() {
        setIsPrivacyConsent(isPrivacyConsentAccepted());
    }
    function updateIsInCTARegion() {
        setIsInCtaRegion(isInCTARegion());
    }
    async function updateIsOnlineAccountAvailable() {
        await getIsAccountAvailable().then((value) => setIsOnlineAccount(value), () => {
            new Error("No Online Account available details found");
        });
    }
    function updateIsInternetConnectionAvailable() {
        setIsInternetConnection(isInternetConnectionAvailable());
    }
    function updateNetworkCostConstraint() {
        setNetworkCostConstraintState(networkCostConstraint());
    }
    function setIsMeteredConnectionUsageAllowedSetting(value) {
        setIsMeteredConnectionUsageAllowed(value);
        window.localStorage.setItem("isMeteredConnectionUsageAllowed", value ? "true" : "false");
        setIsUserCancelMeteredConnection(!value); //Only user explicitly set don't allow metered connection, this value as true.
        (async () => {
            await setIsMeteredConnectionUsageAllowedForMigration(value);
        })();
    }
    function updateIsSignedIn() {
        let previousState = signInState;
        getSignInStatus().then((status) => {
            signInState = status;
            setIsSignedIn(2 /* SignedOut */ !== status);
            // If we're using the new auth API, the local sign in state is managed by Peregrine and needs to be refreshed.
            if (isFeatureEnabled("Feature_AuthApiV5")) {
                if (previousState === 1 /* SignedIn */ && signInState === 2 /* SignedOut */) {
                    requestRefresh();
                }
            }
        }, () => new Error("Unable to get UserSignInStatus from hostapp"));
    }
    async function signIn() {
        authenticator === null || authenticator === void 0 ? void 0 : authenticator.signInUserAsync("clientapp" /*entrypoint for telemetry*/);
    }
    async function signOut() {
        authenticator === null || authenticator === void 0 ? void 0 : authenticator.signOutUserAsync("clientapp" /*entrypoint for telemetry*/);
    }
    function verifyAccount() {
        authenticator === null || authenticator === void 0 ? void 0 : authenticator.verifyAccountAsync("clientapp" /*entrypoint for telemetry*/);
    }
    async function updateReauthRequired() {
        await (authenticator === null || authenticator === void 0 ? void 0 : authenticator.getReauthRequiredStatusAsync().then((isReauthRequiredStatus) => setIsReauthRequired(isReauthRequiredStatus), () => new Error("Unable to get ReauthRequired state")));
    }
    function updateStatuses() {
        try {
            updateIsPrivacyConsented();
            updateIsInCTARegion();
            updateIsInternetConnectionAvailable();
            updateNetworkCostConstraint();
            updateIsOnlineAccountAvailable().catch(() => new Error("No online account status found"));
            updateIsSignedIn();
        }
        catch (error) {
            Measures.logEvent(dashboard, "UpdateStatusesError", 16777216 /* productAndServicePerformance */, {
                error: error
            });
            requestRefresh();
        }
    }
    function setResetMainPage(resetMainPageCallback) {
        resetMainPage.current = resetMainPageCallback;
    }
    async function onNetworkStatusChanged() {
        updateStatuses();
        const statusChangeType = "NetworkStatusChangeEvent";
        Measures.logEvent(dashboard, "StatusChangeEvent", 33554432 /* productAndServiceUsage */, {
            StatusChangeType: statusChangeType,
        });
    }
    const defaultAccountIdRef = useRef("");
    async function updateDefaultAccountId() {
        if (authenticator) {
            const currentAccountId = defaultAccountIdRef.current;
            const newAccountId = await authenticator.getDefaultAccountIdAsync();
            if (newAccountId !== currentAccountId) {
                defaultAccountIdRef.current = newAccountId;
                Measures.logEvent(dashboard, "WebAppDefaultAccountIdChanged", 16777216 /* productAndServicePerformance */, {
                    currentAccountIdEmpty: currentAccountId === "",
                    isReauthRequiredUserEnabled: true,
                    isSignedOutUserEnabled: true,
                    newAccountIdEmpty: newAccountId === "",
                    shouldRefresh: true,
                });
                // Sign-in page has been removed, so we'll be showing the main page regardless of the existence of a default account.
                // So trigger refresh as long as default account changes, even if it's not strictly an identity change.
                await requestRefresh();
            }
        }
    }
    async function onIdentityStatusChanged() {
        updateStatuses();
        const statusChangeType = "IdentityStatusChangeEvent";
        Measures.logEvent(dashboard, "StatusChangeEvent", 33554432 /* productAndServiceUsage */, {
            StatusChangeType: statusChangeType,
        });
        updateDefaultAccountId();
    }
    // Update input state for page management logic with initial values and register listeners such
    // that the state will be updated on any change notifications.
    useEffect(() => {
        updateStatuses();
        authenticator === null || authenticator === void 0 ? void 0 : authenticator.getDefaultAccountIdAsync().then((accountId) => defaultAccountIdRef.current = accountId, () => new Error("Unable to get default account id"));
        addIdentityStatusChangedEventListener(onIdentityStatusChanged).catch(() => new Error("Unable to register for Identity Detail events"));
        addNetworkStatusChangedEventListener(onNetworkStatusChanged).catch(() => new Error("Unable to register for Internet Connection Detail events"));
        return function cleanup() {
            removeIdentityStatusChangedEventListener(onIdentityStatusChanged).catch(() => new Error("Unable to unregister for Identity Detail events"));
            removeNetworkStatusChangedEventListener(onNetworkStatusChanged).catch(() => new Error("Unable to unregister for Internet Connection Detail events"));
        };
    }, []);
    useEffect(() => {
        getUserInfo().then((info) => {
            setUserInfo(info);
        });
    }, []);
    useEffect(() => {
        updateReauthRequired().catch(() => new Error("Unable to get ReauthRequired state"));
        addReauthRequiredStatusChangedEventListener(updateReauthRequired).catch(() => new Error("Unable to register for ReauthRequiredStatusChanged event"));
        return function cleanup() {
            removeReauthRequiredStatusChangedEventListener(updateReauthRequired).catch(() => new Error("Unable to unregister for ReauthRequiredStatusChanged event"));
        };
    }, []);
    // Set up app lifecycle event registrations. These will be used to inform page management
    // decisions and to drive app lifecycle processes such as background refresh and suspend.
    useEffect(() => {
        // This callback will handle background refresh requests from the host. When it is called, the host has
        // woken up to perform a background refresh. The deferral supplied with the eventArgs should be
        // completed when the web app has completed the background refresh.
        const onBackgroundRefresh = async (eventArgs) => {
            Measures.logEvent(dashboard, "WebAppOnBackgroundRefresh", 16777216 /* productAndServicePerformance */);
            // Only perform a widget refresh if the web app is in the "main page" state, since it may not
            // have even loaded widgets otherwise. In the future, we may want to move the logic for deciding this
            // into the orchestrator or provide a way for clients to know whether the orchestrator/widgets have been
            // loaded at all yet.
            if (page.current === "main") {
                Measures.logEvent(dashboard, "WebAppWidgetContentRefresh", 16777216 /* productAndServicePerformance */);
                // Start a refresh of the widgets and wait for the refresh to complete.
                const orchestrator = await getWidgetOrchestrator();
                const isFullPageRefreshRequired = await orchestrator.refreshWidgets().catch((reason) => {
                    // If the widget refresh failed in some way, try doing a full page refresh.
                    return true;
                });
                // If the widget refresh call indicated that a full page refresh was required, then trigger one.
                // Otherwise, just complete the background refresh deferral.
                if (isFullPageRefreshRequired) {
                    Measures.logEvent(dashboard, "WebAppFullPageReload", 16777216 /* productAndServicePerformance */);
                    await requestRefresh();
                }
                else {
                    Measures.logEvent(dashboard, "WebAppCompletingBgRefreshDeferral", 16777216 /* productAndServicePerformance */);
                    if (resetMainPage.current) {
                        resetMainPage.current();
                    }
                    eventArgs.completeDeferral();
                }
            }
        };
        // This callback will handle suspending requests from the host. When it is called, the host is trying to suspend.
        // The deferral supplied with the eventArgs should be completed when the web app is ready to suspend.
        const onSuspending = async (eventArgs) => {
            Measures.logEvent(dashboard, "WebAppOnSuspending", 16777216 /* productAndServicePerformance */);
            if (page.current === "main") {
                const orchestrator = await getWidgetOrchestrator();
                await orchestrator.suspendWidgets();
            }
            Trace.logEvent(dashboard, "WebAppCompletingSuspend", 0 /* verbose */);
            eventArgs.completeDeferral();
        };
        // This callback will handle resume notifications from the host. When it is called, the host has been resumed.
        const onResumed = async (eventArgs) => {
            Measures.logEvent(dashboard, "WebAppOnResumed", 16777216 /* productAndServicePerformance */);
            if (page.current === "main") {
                const orchestrator = await getWidgetOrchestrator();
                orchestrator.resumeWidgets();
            }
        };
        (async () => {
            if (hasPrevPageManagerUnmountedPromise) {
                await hasPrevPageManagerUnmountedPromise;
            }
            hasPrevPageManagerUnmountedPromise = generatePromise(true);
            hasCurrentPageManagerMountedPromise = generatePromise(false);
            try {
                const lifecycleApi = await tryGetHostApi("Lifecycle", 1, 1);
                if (lifecycleApi) {
                    await lifecycleApi.addEventListener("resumed", onResumed);
                    await lifecycleApi.addEventListener("suspending", onSuspending);
                    await lifecycleApi.addEventListener("backgroundrefresh", onBackgroundRefresh);
                }
            }
            catch (error) {
                Trace.logEvent(dashboard, "PageManagerRegisterLifecycleApiError", 0 /* verbose */, {
                    errMsg: (error === null || error === void 0 ? void 0 : error.message) || ""
                });
            }
            currentPageManagerMountedResolver === null || currentPageManagerMountedResolver === void 0 ? void 0 : currentPageManagerMountedResolver();
        })();
        return () => {
            (async () => {
                try {
                    const lifecycleApi = await tryGetHostApi("Lifecycle", 1, 1);
                    // we should guarantee we remove the eventlistener as the same order they are added.
                    // Please check this pr https://microsoft.visualstudio.com/Universal%20Store/_git/MEE.Shell.Dashboard/pullrequest/7612877 to get the reason
                    if (lifecycleApi) {
                        if (hasCurrentPageManagerMountedPromise) {
                            await hasCurrentPageManagerMountedPromise;
                        }
                        await lifecycleApi.removeEventListener("resumed", onResumed);
                        await lifecycleApi.removeEventListener("suspending", onSuspending);
                        await lifecycleApi.removeEventListener("backgroundrefresh", onBackgroundRefresh);
                    }
                }
                catch (error) {
                    Trace.logEvent(dashboard, "PageManagerRemoveLifecycleApiError", 0 /* verbose */, {
                        errMsg: (error === null || error === void 0 ? void 0 : error.message) || ""
                    });
                }
                prevPageManagerUnmountedResolver === null || prevPageManagerUnmountedResolver === void 0 ? void 0 : prevPageManagerUnmountedResolver();
            })();
        };
    }, []);
    function getCurrentPage(isInCtaRegionArg, isPrivacyConsentArg, isOnlineAccountArg, isInternetConnectionArg, networkCostConstraintStateArg, isMeteredConnectionUsageAllowedArg, isUserCancelMeteredConnectionArg, isSignedInArg) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlParamsPage = urlParams.get("page");
        if (urlParamsPage) {
            return urlParamsPage;
        }
        if (isInCtaRegionArg === null ||
            isPrivacyConsentArg === null ||
            isOnlineAccountArg === null ||
            isInternetConnectionArg === null ||
            networkCostConstraintStateArg === null) {
            return "empty-page";
        }
        if (!isPrivacyConsentArg && isInCtaRegionArg) {
            return "loc-net-cta";
        }
        if (isFeatureEnabled("Feature_CTAOfflinePage")) {
            if (!isInternetConnectionArg && (page.current === "empty-page" || page.current === "global-offline")) {
                // WidgetsOffline event is very useful; do not remove
                Measures.logEvent(dashboard, "WidgetsOffline", 16777216 /* productAndServicePerformance */, {
                    isInCtaRegionArg: isInCtaRegionArg,
                    isInternetConnectionArg: isInternetConnectionArg,
                    isMeteredConnectionUsageAllowedArg: isMeteredConnectionUsageAllowedArg,
                    networkCostConstraintStateArg: networkCostConstraintStateArg,
                    page: page.current,
                });
                return "global-offline";
            }
            // In CTA region with metered connection, widgets board will first show CTA offline page to ask if user allow metered connection usage
            // If user explicitly select "cancel", then the board will display global offline page.
            else if (isInCtaRegionArg && !isMeteredConnectionUsageAllowedArg &&
                (networkCostConstraintStateArg === "conservative" || networkCostConstraintStateArg === "optIn")) {
                Measures.logEvent(dashboard, isUserCancelMeteredConnectionArg ? "WidgetsOffline" : "CTAOffline", 16777216 /* productAndServicePerformance */, {
                    isInCtaRegionArg: isInCtaRegionArg,
                    isInternetConnectionArg: isInternetConnectionArg,
                    isMeteredConnectionUsageAllowedArg: isMeteredConnectionUsageAllowedArg,
                    isUserCancelMeteredConnectionArg: isUserCancelMeteredConnectionArg,
                    networkCostConstraintStateArg: networkCostConstraintStateArg,
                    page: page.current,
                });
                return isUserCancelMeteredConnectionArg ? "global-offline" : "cta-offline";
            }
        }
        else {
            if ((!isInternetConnectionArg && (page.current === "empty-page" || page.current === "global-offline")) ||
                (isInCtaRegionArg &&
                    !isMeteredConnectionUsageAllowedArg &&
                    (networkCostConstraintStateArg === "conservative" || networkCostConstraintStateArg === "optIn"))) {
                // WidgetsOffline event is very useful; do not remove
                Measures.logEvent(dashboard, "WidgetsOffline", 16777216 /* productAndServicePerformance */, {
                    isInCtaRegionArg: isInCtaRegionArg,
                    isInternetConnectionArg: isInternetConnectionArg,
                    isMeteredConnectionUsageAllowedArg: isMeteredConnectionUsageAllowedArg,
                    networkCostConstraintStateArg: networkCostConstraintStateArg,
                    page: page.current,
                });
                return "global-offline";
            }
        }
        return "main";
    }
    // Calculate what the current page should be and update it, saving the previous page
    // value so that it can be determined whether the page changed or not.
    const prevPage = page.current;
    page.current = getCurrentPage(isInCtaRegion, isPrivacyConsent, isOnlineAccount, isInternetConnection, networkCostConstraintState, isMeteredConnectionUsageAllowed, isUserCancelMeteredConnection, isSignedIn);
    Trace.logEvent(dashboard, "WebAppUpdatePage", 0 /* verbose */, {
        isInCtaRegion: isInCtaRegion,
        isInternetConnection: isInternetConnection,
        isMeteredConnectionUsageAllowed: isMeteredConnectionUsageAllowed,
        isOnlineAccount: isOnlineAccount,
        isPrivacyConsent: isPrivacyConsent,
        isReauthRequired: isReauthRequired,
        isReauthRequiredUserEnabled: true,
        isSignedIn: isSignedIn,
        isSignedOutUserEnabled: true,
        networkCostConstraintState: networkCostConstraintState,
        newPage: page.current,
    });
    // If the page changed, run any logic that should happen on page transition.
    if (page.current !== prevPage) {
        Measures.logEvent(dashboard, "WebAppPageChange", 33554432 /* productAndServiceUsage */, {
            newPage: page.current,
            prevPage: prevPage,
        });
        if (page.current === "global-offline") {
            Measures.logEventWithLevelAndTag(dashboard, "EnterGlobalOfflinePageVisible", 33554432 /* productAndServiceUsage */, 0 /* verbose */, 0 /* none */);
        }
        else if (prevPage === "global-offline") {
            Measures.logEventWithLevelAndTag(dashboard, "ExitGlobalOfflinePageVisible", 33554432 /* productAndServiceUsage */, 0 /* verbose */, 0 /* none */);
        }
    }
    const [isContentDisplayReady, setIsContentDisplayReady] = useState(false);
    const [isFeedReady, setIsFeedReady] = useState(false);
    // In dynamicFeed, there is no widgetContainer so we set the container to ready immediately
    const [isWidgetContainerReady, setIsWidgetContainerReady] = useState(true);
    // Set up the content ready notification to occur once the layout of the page is complete.
    useEffect(() => {
        if (isContentDisplayReady === false) {
            if (page.current === "main") {
                if (isWidgetContainerReady && isFeedReady) {
                    Measures.logEventWithLevelAndTag(dashboard, "WebAppContentDisplayReady", 16777216 /* productAndServicePerformance */, 0 /* verbose */, 0 /* none */);
                    setIsContentDisplayReady(true);
                    notifyContentDisplayReady();
                }
            }
            else if (page.current !== "empty-page") {
                setIsContentDisplayReady(true);
                notifyContentDisplayReady();
            }
        }
    }, [isWidgetContainerReady, isFeedReady, page.current]);
    switch (page.current) {
        case "empty-page":
        default:
            return React.createElement(EmptyPage, null);
        case "main":
            return (React.createElement(React.Fragment, null, (page.current === "main") && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
                React.createElement(ContentReadyContext.Provider, { value: {
                        isFeedReady: isFeedReady,
                        isWidgetContainerReady: isWidgetContainerReady,
                        setIsFeedReady: setIsFeedReady,
                        setIsWidgetContainerReady: setIsWidgetContainerReady,
                    } },
                    React.createElement(LocalSignInContext.Provider, { value: {
                            isReauthRequired: isReauthRequired,
                            isUserSignedIn: isSignedIn,
                            signIn: signIn,
                            signOut: signOut,
                            userInfo: userInfo,
                            verifyAccount: verifyAccount,
                        } },
                        React.createElement(CtaSettingContext.Provider, { value: {
                                isInCtaRegion: isInCtaRegion,
                                isMeteredConnectionUsageAllowed: isMeteredConnectionUsageAllowed,
                                isUserCancelMeteredConnection: isUserCancelMeteredConnection,
                                setIsMeteredConnectionUsageAllowedSetting: setIsMeteredConnectionUsageAllowedSetting,
                            } },
                            React.createElement(MainPage, { onClick: (e) => onClick(e), setResetMainPageCallback: setResetMainPage, isAvoidRenderWhileHiddenEnabled: isAvoidRenderWhileHiddenEnabled }))))))));
        case "loc-net-cta":
            return (React.createElement(React.Fragment, null, (page.current === "loc-net-cta") && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
                React.createElement(LocationNetworkCTAPage, { onClick: (e) => onClick(e) }),
                ";"))));
        case "global-offline":
            return (React.createElement(React.Fragment, null, (page.current === "global-offline") && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
                React.createElement(GlobalOfflinePage, { onClick: (e) => onClick(e) }),
                ";"))));
        case "cta-offline":
            return (React.createElement(React.Fragment, null, (page.current === "cta-offline") && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
                React.createElement(CtaSettingContext.Provider, { value: {
                        isInCtaRegion: isInCtaRegion,
                        isMeteredConnectionUsageAllowed: isMeteredConnectionUsageAllowed,
                        isUserCancelMeteredConnection: isUserCancelMeteredConnection,
                        setIsMeteredConnectionUsageAllowedSetting: setIsMeteredConnectionUsageAllowedSetting,
                    } },
                    React.createElement(CTAOfflinePage, { onClick: (e) => onClick(e) }),
                    ";")))));
        case "temp-disablement":
            return (React.createElement(React.Fragment, null, (page.current === "temp-disablement") && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
                React.createElement(TempDisablementPage, { onClick: (e) => onClick(e) })))));
    }
};
